import React, { useState } from 'react'
import type { Cliente } from '../../lib/supabase'

interface ModalAlterarStatusProps {
  cliente: Cliente
  isOpen: boolean
  onClose: () => void
  onConfirm: (novoStatus: 'ativo' | 'inativo', dataInativacao?: string) => void
}

export default function ModalAlterarStatus({ cliente, isOpen, onClose, onConfirm }: ModalAlterarStatusProps) {
  const [dataInativacao, setDataInativacao] = useState('')
  const [loading, setLoading] = useState(false)

  if (!isOpen) return null

  const novoStatus = cliente.status === 'ativo' ? 'inativo' : 'ativo'
  const isInativando = novoStatus === 'inativo'

  const handleConfirm = async () => {
    if (isInativando && !dataInativacao) {
      alert('Data de inativação é obrigatória')
      return
    }

    if (isInativando && new Date(dataInativacao) > new Date()) {
      alert('Data de inativação não pode ser futura')
      return
    }

    setLoading(true)
    try {
      await onConfirm(novoStatus, isInativando ? dataInativacao : undefined)
      onClose()
    } catch (error) {
      console.error('Erro ao alterar status:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const day = date.getUTCDate().toString().padStart(2, '0')
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
    const year = date.getUTCFullYear()
    return `${day}/${month}/${year}`
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        <div className="text-center mb-6">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
            isInativando ? 'bg-red-100' : 'bg-green-100'
          }`}>
            {isInativando ? (
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>
          
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {isInativando ? 'Inativar Cliente' : 'Reativar Cliente'}
          </h3>
          
          <p className="text-gray-600 mb-4">
            {cliente.nome_completo || cliente.nome_empresa}
          </p>

          {isInativando && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div className="text-left">
                  <p className="text-sm font-medium text-yellow-800">Atenção!</p>
                  <p className="text-sm text-yellow-700 mt-1">
                    Clientes inativos não aparecerão nas projeções de recebimento futuras.
                    O histórico de recebimentos será mantido.
                  </p>
                </div>
              </div>
            </div>
          )}

          {cliente.status === 'inativo' && cliente.data_inativacao && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600">
                <strong>Inativado em:</strong> {formatDate(cliente.data_inativacao)}
              </p>
            </div>
          )}
        </div>

        {isInativando && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data de Inativação *
            </label>
            <input
              type="date"
              value={dataInativacao}
              onChange={(e) => setDataInativacao(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Data em que o cliente deve ser considerado inativo
            </p>
          </div>
        )}

        <div className="flex space-x-4">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className={`flex-1 px-6 py-3 text-white rounded-lg font-medium transition-colors disabled:opacity-50 ${
              isInativando 
                ? 'bg-red-600 hover:bg-red-700' 
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {loading ? 'Processando...' : (isInativando ? 'Inativar' : 'Reativar')}
          </button>
        </div>
      </div>
    </div>
  )
}
